import { useCallback, useEffect, useState } from 'react';
import type { TermsAgreement } from '@zip2live/shared/safety';
import { disablePush } from './follow.ts';
import { forgetAgreement } from './rules.ts';

export interface Me {
  user: {
    isAnonymous: boolean;
    isVerified: boolean;
    email: string | null;
    phoneNumber: string | null;
    /** The rules they agreed to and their age group (null: never asked). */
    terms: TermsAgreement | null;
  } | null;
  channel: {
    displayName: string | null;
    handle: string | null;
    bio: string | null;
    links: { url: string; label: string }[];
    photoUrl: string | null;
    followers?: number;
    /** The title the next stream starts with (the last one used). */
    defaultTitle: string | null;
  } | null;
  streams: {
    id: string; title: string | null; status: string; source: string; created_at: number; started_at: number | null; ended_at: number | null;
    /** 'public': listed on Live Now while live. 'private': only the creator can watch the replay. */
    visibility: 'unlisted' | 'public' | 'private';
    has_replay: boolean;
    /** The replay's retention ran out and its video is gone. */
    replay_expired: boolean;
    /** When the replay will be deleted (24 h for guests, 30 days signed in). */
    replay_expires_at: number | null;
    removed: boolean;
    /** What the AI scan says the stream is about (see STREAM_CATEGORIES). */
    category: string | null;
    peak_viewers: number;
    views: number;
    likes: number;
    dislikes: number;
    reactions: number;
    /** Latest preview picture (16:9 JPEG), or null. */
    thumbnailUrl: string | null;
  }[];
}

let cached: Me | null = null;
const listeners = new Set<(m: Me | null) => void>();

export async function refreshMe(): Promise<Me | null> {
  const res = await fetch('/api/v1/me', { credentials: 'include' }).catch(() => null);
  cached = res?.ok ? ((await res.json()) as Me) : null;
  for (const l of listeners) l(cached);
  return cached;
}

/** Who is using the app right now: nobody yet, a guest on this device, or a signed-in account. */
export function useMe() {
  const [me, setMe] = useState<Me | null>(cached);
  const [loaded, setLoaded] = useState(cached !== null);
  useEffect(() => {
    const on = (m: Me | null) => {
      setMe(m);
      setLoaded(true);
    };
    listeners.add(on);
    if (!cached) void refreshMe();
    return () => void listeners.delete(on);
  }, []);
  const reload = useCallback(() => refreshMe(), []);
  return { me, loaded, reload };
}

export async function signOut() {
  // This browser stops getting the account's go-live alerts (shared computers).
  await disablePush().catch(() => undefined);
  await fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
  forgetAgreement();
  cached = null;
  await refreshMe();
}
